import React, { useState, useEffect } from 'react';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  MapPin, 
  User, 
  Home, 
  Calendar,
  Download,
  ChevronLeft,
  FileText,
  Check,
  X,
  AlertCircle,
  Utensils,
  Droplets,
  Shield,
  Wrench,
  Bath,
  Trash2,
  Thermometer,
  Eye,
  MessageCircle,
  Navigation,
  Globe
} from 'lucide-react';
import { useLanguage } from "../../context/LanguageContext";

const KioskSurveyDetail = ({ 
  survey, 
  detail, 
  onBack,
  loading 
}) => {
  const { t } = useLanguage();
  const [locationName, setLocationName] = useState(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [showMap, setShowMap] = useState(false);

  useEffect(() => {
    if (detail?.info?.lattitude_longitude) {
      fetchLocationFromCoords(detail.info.lattitude_longitude);
    }
  }, [detail?.info?.lattitude_longitude]);

  const fetchLocationFromCoords = async (coords) => {
    if (!coords || !coords.includes(',')) {
      setLocationName(t.global.invalid_coordinates || "Invalid coordinates");
      return;
    }

    try {
      setMapLoading(true);
      const [lat, lng] = coords.split(',').map(coord => coord.trim());
      const apiKey = process.env.REACT_APP_MAP_API_KEY;
      
      if (!apiKey) {
        console.warn("Google Maps API key not found");
        setLocationName(`${t.global.location_label}: ${coords}`);
        return;
      }

      // Reverse geocoding to get address from coordinates
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`
      );
      
      const data = await response.json();
      
      if (data.status === 'OK' && data.results.length > 0) {
        // Get the most relevant address (formatted address)
        const address = data.results[0].formatted_address;
        setLocationName(address);
      } else {
        setLocationName(`${t.global.location_label}: ${coords}`);
      }
    } catch (error) {
      console.error("Error fetching location:", error);
      setLocationName(`${t.global.location_label}: ${coords}`);
    } finally {
      setMapLoading(false);
    }
  };

  if (!survey || !detail) return null;

  // Status badge component
  const StatusBadge = ({ value, showText = true }) => {
    if (value === "Yes" || value === "1" || value === 1 || value === true) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <Check size={12} className="mr-1" />
          {showText && t.global.yes}
        </span>
      );
    } else if (value === "No" || value === "0" || value === 0 || value === false) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <X size={12} className="mr-1" />
          {showText && t.global.no}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        <Clock size={12} className="mr-1" />
        {showText && t.global.pending}
      </span>
    );
  };

  // Rating badge for quality/quantity ratings
  const RatingBadge = ({ value }) => {
    const colors = {
      'Good': 'bg-green-100 text-green-800',
      'Satisfactory': 'bg-yellow-100 text-yellow-800',
      'Poor': 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${colors[value] || 'bg-gray-100 text-gray-800'}`}>
        {value}
      </span>
    );
  };

  const renderField = (label, value, icon = null, isRating = false) => {
    if (value === null || value === undefined || value === "") return null;
    
    const displayValue = () => {
      if (typeof value === 'boolean') return value ? t.global.yes : t.global.no;
      if (value === "1") return t.global.yes;
      if (value === "0") return t.global.no;
      return value;
    };

    return (
      <div className="bg-white rounded-xl p-4 mb-3 border border-gray-200 shadow-sm">
        <div className="flex items-start">
          {icon && <div className="mr-3 text-blue-600 mt-0.5">{icon}</div>}
          <div className="flex-1">
            <div className="text-sm font-medium text-gray-700 mb-2">{label}</div>
            <div className="flex items-center justify-between">
              {isRating ? (
                <RatingBadge value={displayValue()} />
              ) : (
                <div className="text-gray-900 font-medium text-lg">{displayValue()}</div>
              )}
              {(displayValue() === t.global.yes || displayValue() === t.global.no || displayValue() === t.global.pending) && !isRating && (
                <StatusBadge value={displayValue()} showText={false} />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderUploadField = (label, url) => {
    if (!url) return null;
    
    return (
      <div className="bg-white rounded-xl p-4 mb-3 border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-medium text-gray-700 mb-1">{label}</div>
            <div className="text-xs text-gray-500">{t.global.document_available}</div>
          </div>
          <a 
            href={url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center bg-blue-50 hover:bg-blue-100 text-blue-700 px-3 py-2 rounded-lg font-medium text-sm transition"
          >
            <Download size={14} className="mr-2" />
            {t.global.view}
          </a>
        </div>
      </div>
    );
  };

  const renderSection = (sectionKey, sectionData) => {
    if (!sectionData || Object.keys(sectionData).length === 0) return null;

    // Get section icon based on section key
    const getSectionIcon = (key) => {
      const icons = {
        gen_data: <FileText size={20} />,
        assessment: <Utensils size={20} />,
        water_safety: <Droplets size={20} />,
        gen_clean: <Shield size={20} />,
        safety_arrangement: <Wrench size={20} />,
        sanitation: <Bath size={20} />,
        waste_management: <Trash2 size={20} />,
        hygiene: <Thermometer size={20} />,
        food_hygiene: <Utensils size={20} />,
        iec_monitoring: <Eye size={20} />,
        others: <MessageCircle size={20} />
      };
      return icons[key] || <FileText size={20} />;
    };

    const getSectionTitle = (key) => {
      const titles = {
        gen_data: t.global.section_general_info,
        assessment: t.global.section_food_quality,
        water_safety: t.global.section_water_safety,
        gen_clean: t.global.section_cleanliness,
        safety_arrangement: t.global.section_safety,
        sanitation: t.global.section_sanitation,
        waste_management: t.global.section_waste_management,
        hygiene: t.global.section_hygiene,
        food_hygiene: t.global.section_food_serving,
        iec_monitoring: t.global.section_iec_monitoring,
        others: t.global.section_feedback
      };
      return titles[key] || key.replace(/_/g, ' ');
    };

    // Filter display fields (exclude completion fields)
    const displayFields = Object.entries(sectionData).filter(([key]) => 
      !key.startsWith('is_') && !key.includes('_done')
    );

    if (displayFields.length === 0) return null;

    return (
      <div key={sectionKey} className="bg-white rounded-xl p-5 mb-4 border border-gray-200 shadow-sm">
        <div className="flex items-center mb-5 pb-4 border-b border-gray-100">
          <div className="p-3 bg-blue-50 rounded-xl mr-4">
            {getSectionIcon(sectionKey)}
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">{getSectionTitle(sectionKey)}</h3>
            <p className="text-sm text-gray-500">{t.global.survey_section} {(sectionKey === 'gen_data' ? 'A' : 
              sectionKey === 'assessment' ? 'B' :
              sectionKey === 'water_safety' ? 'C' :
              sectionKey === 'gen_clean' ? 'D' :
              sectionKey === 'safety_arrangement' ? 'E' :
              sectionKey === 'sanitation' ? 'F' :
              sectionKey === 'waste_management' ? 'G' :
              sectionKey === 'hygiene' ? 'H' :
              sectionKey === 'food_hygiene' ? 'I' :
              sectionKey === 'iec_monitoring' ? 'J' : '')}</p>
          </div>
        </div>
        
        <div className="space-y-4">
          {displayFields.map(([key, value]) => {
            if (value === null || value === undefined || value === "") return null;
            
            const getQuestionLabel = (fieldKey) => {
              const questionMappings = {
                // General Information (A)
                'gen_info_A1': t.global.q_functional,
                'gen_info_A1_remarks': t.global.q_remarks_non_functioning,
                'gen_info_A1_upload': t.global.q_supporting_orders,
                'gen_info_A2': t.global.q_operated_by,
                'gen_info_A3': t.global.q_staff_engaged,
                'gen_info_A4': t.global.q_service_timing,
                'gen_info_A5': t.global.q_avg_beneficiaries,
                'gen_info_A6': t.global.q_coupons_preserved,
                
                // Assessment (B)
                'quali_quanti_asmnt_B1': t.global.q_quality_meal,
                'quali_quanti_asmnt_B2': t.global.q_quantity_meal,
                'quali_quanti_asmnt_B3': t.global.q_all_items_served,
                
                // Water Safety (C)
                'water_safety_C1': t.global.q_safe_water,
                'water_safety_C2': t.global.q_water_arrangement,
                'water_safety_C2_upload': t.global.q_water_point_picture,
                'water_safety_C3': t.global.q_water_testing,
                'water_safety_C3_upload': t.global.q_test_report,
                'water_safety_C3_date': t.global.q_last_testing_date,
                
                // Cleanliness (D)
                'gen_clean_D1': t.global.q_adequate_space,
                'gen_clean_D2': t.global.q_hygiene_maintained,
                'gen_clean_D2_remark': t.global.q_hygiene_gaps,
                'gen_clean_D3_upload': t.global.q_housekeeping_record,
                'gen_clean_D4': t.global.q_cleanliness_rating,
                
                // Safety (E)
                'safety_arngmnt_E1': t.global.q_overhead_shed,
                'safety_arngmnt_E2': t.global.q_location_safe,
                
                // Sanitation (F)
                'sanitation_F1': t.global.q_functional_toilet,
                'sanitation_F2': t.global.q_toilet_ventilated,
                'sanitation_F3': t.global.q_toilets_cleaned,
                'sanitation_F3_upload': t.global.q_housekeeping_record,
                'sanitation_F4': t.global.q_covered_dustbin,
                'sanitation_F4_upload': t.global.q_dustbin_picture,
                
                // Waste Management (G)
                'wst_mngmnt_G1': t.global.q_colored_bins,
                'wst_mngmnt_G2_upload': t.global.q_iec_display,
                'wst_mngmnt_G3': t.global.q_bins_cleaned,
                'wst_mngmnt_G3_upload': t.global.q_housekeeping_checklist,
                'wst_mngmnt_G4': t.global.q_single_use_plastic,
                
                // Hygiene (H)
                'hygiene_H1': t.global.q_protective_gear,
                'hygiene_H1_upload': t.global.q_ppe_picture,
                'hygiene_H2': t.global.q_handwashing_station,
                'hygiene_H2_upload': t.global.q_handwashing_picture,
                'hygiene_H3': t.global.q_water_stagnation,
                'hygiene_H4': t.global.q_handwashing_practice,
                'hygiene_H5': t.global.q_health_checkups,
                'hygiene_H6': t.global.q_personal_hygiene,
                
                // Food Hygiene (I)
                'food_hygine_I1': t.global.q_plate_type,
                
                // IEC & Monitoring (J)
                'iec_monitoring_J1': t.global.q_posters_displayed,
                'iec_monitoring_J2': t.global.q_online_entry,
                'iec_monitoring_J3': t.global.q_coupon_reconciliation,
                
                // Others
                'challenges': t.global.q_challenges,
                'suggestions': t.global.q_suggestions
              };
              
              return questionMappings[fieldKey] || fieldKey.replace(/_/g, ' ');
            };

            const getFormattedValue = (fieldKey, val) => {
              // Handle specific field formats
              if (fieldKey === 'gen_info_A2') {
                if (val === '0') return t.global.shgs;
                if (val === '1') return t.global.others;
                return val;
              }
              
              if (fieldKey === 'food_hygine_I1') {
                const plateTypes = {
                  '0': t.global.not_applicable,
                  '1': t.global.plate_thermocol,
                  '2': t.global.plate_aluminium_foil,
                  '3': t.global.plate_other,
                  'Yes': t.global.yes
                };
                return plateTypes[val] || val;
              }
              
              // Format time values
              if (fieldKey === 'gen_info_A4' && val) {
                try {
                  const timeParts = val.split(':');
                  if (timeParts.length >= 2) {
                    const hours = parseInt(timeParts[0]);
                    const minutes = timeParts[1];
                    const ampm = hours >= 12 ? 'PM' : 'AM';
                    const displayHours = hours % 12 || 12;
                    return `${displayHours}:${minutes} ${ampm}`;
                  }
                } catch (e) {
                  // If parsing fails, return original value
                }
              }
              
              return val;
            };

            const isRatingField = key.includes('B1') || key.includes('B2') || key.includes('D4');
            
            if (key.includes('upload')) {
              return renderUploadField(getQuestionLabel(key), value);
            }
            
            return renderField(
              getQuestionLabel(key), 
              getFormattedValue(key, value),
              null,
              isRatingField
            );
          })}
        </div>
      </div>
    );
  };

  const renderLocationCard = () => {
    const coords = detail?.info?.lattitude_longitude;
    const apiKey = process.env.REACT_APP_MAP_API_KEY;
    
    if (!coords) return null;

    return (
      <div className="bg-white rounded-xl p-5 mb-4 border border-gray-200 shadow-sm">
        <div className="flex items-center mb-4">
          <Globe className="text-blue-600 mr-3" size={24} />
          <h3 className="text-xl font-bold text-gray-900">{t.global.location_details}</h3>
        </div>
        
        <div className="space-y-4">
          {/* Coordinates Display */}
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                <MapPin className="text-blue-700 mr-2" size={18} />
                <div className="text-sm font-medium text-blue-700">{t.global.coordinates}</div>
              </div>
              <div className="text-xs text-blue-600 bg-white px-2 py-1 rounded">
                {t.global.lat_long}
              </div>
            </div>
            <div className="font-mono text-sm bg-white p-3 rounded border border-blue-200 text-gray-800">
              {coords}
            </div>
            {mapLoading ? (
              <div className="mt-3 text-sm text-gray-600 flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                {t.global.fetching_location}
              </div>
            ) : locationName && (
              <div className="mt-3 text-sm text-gray-700 bg-white p-3 rounded border border-blue-100">
                📍 {locationName}
              </div>
            )}
          </div>

          {/* Interactive Map Section */}
          {apiKey && (
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <Navigation className="text-gray-700 mr-2" size={18} />
                  <div className="text-sm font-medium text-gray-700">{t.global.interactive_map}</div>
                </div>
                <button
                  onClick={() => setShowMap(!showMap)}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center"
                >
                  {showMap ? t.global.hide_map : t.global.show_map}
                </button>
              </div>
              
              {showMap && (
                <div className="mt-3 border border-gray-300 rounded-lg overflow-hidden">
                  <iframe
                    title="Kiosk Location Map"
                    width="100%"
                    height="250"
                    frameBorder="0"
                    style={{ border: 0 }}
                    src={`https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${coords}&zoom=15&maptype=roadmap`}
                    allowFullScreen
                  />
                </div>
              )}
              
              {/* Open in Google Maps link */}
              <div className="mt-3">
                <a
                  href={`https://www.google.com/maps?q=${coords}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium text-sm"
                >
                  <Navigation size={14} className="mr-2" />
                  {t.global.open_in_google_maps}
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCompletionStats = () => {
    const sections = [
      { key: 'gen_data', title: t.global.section_general_info_short, letter: 'A' },
      { key: 'assessment', title: t.global.section_food_quality_short, letter: 'B' },
      { key: 'water_safety', title: t.global.section_water_safety_short, letter: 'C' },
      { key: 'gen_clean', title: t.global.section_cleanliness_short, letter: 'D' },
      { key: 'safety_arrangement', title: t.global.section_safety_short, letter: 'E' },
      { key: 'sanitation', title: t.global.section_sanitation_short, letter: 'F' },
      { key: 'waste_management', title: t.global.section_waste_mgmt_short, letter: 'G' },
      { key: 'hygiene', title: t.global.section_hygiene_short, letter: 'H' },
      { key: 'food_hygiene', title: t.global.section_serving_short, letter: 'I' },
      { key: 'iec_monitoring', title: t.global.section_iec_short, letter: 'J' }
    ];

    const completedSections = sections.filter(section => {
      const sectionData = detail[section.key];
      if (!sectionData) return false;
      
      // Handle different completion key formats based on section
      const getCompletionValue = (data) => {
        // Check for completion field (note: "food_hygine" is misspelled in API)
        if (section.key === 'food_hygiene') {
          return data['is_food_hygine_survey_done'];
        }
        
        // For other sections
        const completionKeys = {
          gen_data: 'is_gen_info_survey_done',
          assessment: 'is_quali_and_quanti_assessment_survey_done',
          water_safety: 'is_water_safety_survey_done',
          gen_clean: 'is_gen_clean_survey_done',
          safety_arrangement: 'is_safety_arngmnt_survey_done',
          sanitation: 'is_sanitation_survey_done',
          waste_management: 'is_wst_mngmnt_survey_done',
          hygiene: 'is_hygiene_survey_done',
          iec_monitoring: 'is_iec_monitoring_survey_done'
        };
        
        const key = completionKeys[section.key];
        return data[key];
      };
      
      const completionValue = getCompletionValue(sectionData);
      return completionValue === "1" || completionValue === 1 || completionValue === true;
    }).length;

    const totalSections = sections.length;
    const completionPercentage = totalSections > 0 ? Math.round((completedSections / totalSections) * 100) : 0;

    return (
      <div className="bg-white rounded-xl p-5 mb-4 border border-gray-200 shadow-sm">
        <h3 className="text-xl font-bold text-gray-900 mb-5">{t.global.survey_progress}</h3>
        
        {/* Section Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {sections.map(section => {
            const sectionData = detail[section.key];
            if (!sectionData) return null;
            
            // Get completion status
            let isCompleted = false;
            
            if (section.key === 'food_hygiene') {
              isCompleted = sectionData['is_food_hygine_survey_done'] === "1" || 
                           sectionData['is_food_hygine_survey_done'] === 1 || 
                           sectionData['is_food_hygine_survey_done'] === true;
            } else {
              const completionKeys = {
                gen_data: 'is_gen_info_survey_done',
                assessment: 'is_quali_and_quanti_assessment_survey_done',
                water_safety: 'is_water_safety_survey_done',
                gen_clean: 'is_gen_clean_survey_done',
                safety_arrangement: 'is_safety_arngmnt_survey_done',
                sanitation: 'is_sanitation_survey_done',
                waste_management: 'is_wst_mngmnt_survey_done',
                hygiene: 'is_hygiene_survey_done',
                iec_monitoring: 'is_iec_monitoring_survey_done'
              };
              
              const completionKey = completionKeys[section.key];
              const completionValue = sectionData[completionKey];
              isCompleted = completionValue === "1" || completionValue === 1 || completionValue === true;
            }
            
            return (
              <div 
                key={section.key} 
                className={`text-center p-3 rounded-lg border-2 transition-all ${
                  isCompleted 
                    ? 'border-green-200 bg-green-50' 
                    : 'border-gray-200 bg-gray-50'
                }`}
              >
                <div className={`text-lg font-bold mb-1 ${
                  isCompleted ? 'text-green-600' : 'text-gray-400'
                }`}>
                  {section.letter}
                </div>
                <div className="text-xs font-medium text-gray-700">
                  {section.title}
                </div>
                <div className={`text-xs mt-1 font-medium ${
                  isCompleted ? 'text-green-600' : 'text-gray-500'
                }`}>
                  {isCompleted ? t.global.done : t.global.pending}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 shadow-sm">
        <div className="px-4 py-4">
          <div className="flex items-center mb-3">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg mr-3"
            >
              <ChevronLeft size={24} />
            </button>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900">{t.global.survey_details}</h1>
              <p className="text-sm text-gray-500">{t.global.survey_id_label} #{survey.id}</p>
            </div>
            <div className={`px-4 py-2 rounded-full font-bold ${
              detail.info?.is_total_survey_complete === 1 
                ? 'bg-green-100 text-green-700' 
                : 'bg-yellow-100 text-yellow-700'
            }`}>
              {detail.info?.is_total_survey_complete === 1 ? t.global.completed : t.global.in_progress}
            </div>
          </div>
          
          <div className="flex items-center text-sm text-gray-600">
            <Calendar size={16} className="mr-2" />
            <span>
              {new Date(survey.created_at).toLocaleDateString('en-IN', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              })}
              {' • '}
              {new Date(survey.created_at).toLocaleTimeString('en-IN', {
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 pb-24">
        {loading ? (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto"></div>
            <p className="text-gray-600 mt-4 font-medium">{t.global.loading_survey_details}</p>
          </div>
        ) : (
          <>
            {/* Basic Info Card */}
            <div className="bg-white rounded-xl p-5 mb-4 border border-gray-200 shadow-sm">
              <div className="flex items-center mb-5">
                <Home className="text-blue-600 mr-3" size={24} />
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{t.global.kiosk_information}</h2>
                  <p className="text-sm text-gray-500">{t.global.maa_programme_inspection}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {renderField(t.global.kiosk_name, detail.info?.area_name, <Home size={16} />)}
                {renderField(t.global.kiosk_id, detail.info?.id, <FileText size={16} />)}
                {renderField(t.global.canteen_type, detail.info?.canteen_type_name)}
                {renderField(t.global.district, detail.info?.district_name)}
                {renderField(t.global.municipality_ulb, detail.info?.municipality_name)}
                {renderField(t.global.ward_no, detail.info?.ward_name)}
                {renderField(t.global.surveyor, detail.info?.fullName, <User size={16} />)}
                <div className="col-span-1 md:col-span-2">
                  <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
                    <div className="text-sm font-medium text-blue-700 mb-1">{t.global.reporting_date}</div>
                    <div className="text-blue-900 font-medium">
                      {detail.info?.reporting_date || t.global.not_specified}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Location Card (using lat/long) */}
            {renderLocationCard()}

            {/* Survey Sections */}
            <div className="mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 px-2">{t.global.all_inspection_sections}</h2>
              <div className="space-y-4">
                {['gen_data', 'assessment', 'water_safety', 'gen_clean', 'safety_arrangement', 
                  'sanitation', 'waste_management', 'hygiene', 'food_hygiene', 'iec_monitoring', 'others']
                  .map(sectionKey => {
                    if (detail[sectionKey]) {
                      return renderSection(sectionKey, detail[sectionKey]);
                    }
                    return null;
                  })}
              </div>
            </div>

            {/* Survey Progress Section */}
            {renderCompletionStats()}

            {/* Overall Status */}
            {detail.info?.is_total_survey_complete === 1 && (
              <div className="bg-green-50 border-2 border-green-200 rounded-xl p-5 mb-4">
                <div className="flex items-center">
                  <CheckCircle className="text-green-600 mr-3" size={24} />
                  <div>
                    <h3 className="text-lg font-bold text-green-800">{t.global.survey_completed_submitted}</h3>
                    <p className="text-green-600">{t.global.all_sections_submitted}</p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Fixed Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg">
        <button
          onClick={onBack}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-bold text-lg flex items-center justify-center transition duration-200"
        >
          <ChevronLeft className="mr-2" size={20} />
          {t.global.back_to_survey_list}
        </button>
      </div>
    </div>
  );
};

export default KioskSurveyDetail;